import { apiSlice } from './apiSlice';
import type {
  EventTemplateDetail,
  EventTemplatesResponse,
  EventTemplateStatsResponse,
  CreateEventTemplatePayload,
  UpdateEventTemplatePayload,
  EventTemplatesQueryParams,
} from '../types/eventTemplates';

/**
 * Event Templates API — save-scoped (Wave 5.1).
 * All endpoints live under /api/saves/:saveId/event-templates and the caller
 * provides the active saveId. System templates are visible to everyone with
 * `saves.read`; save-scoped templates require save-membership.
 */
export const eventTemplatesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEventTemplates: builder.query<
      EventTemplatesResponse,
      EventTemplatesQueryParams & { saveId: string }
    >({
      query: ({ saveId, ...params }) => ({
        url: `/saves/${saveId}/event-templates`,
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.templates.map(({ id }) => ({ type: 'EventTemplate' as const, id })),
              { type: 'EventTemplate', id: 'LIST' },
            ]
          : [{ type: 'EventTemplate', id: 'LIST' }],
    }),

    getEventTemplateById: builder.query<EventTemplateDetail, { id: number; saveId: string }>({
      query: ({ id, saveId }) => ({
        url: `/saves/${saveId}/event-templates/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [{ type: 'EventTemplate', id }],
    }),

    createEventTemplate: builder.mutation<
      EventTemplateDetail,
      CreateEventTemplatePayload & { saveId: string }
    >({
      query: ({ saveId, ...payload }) => ({
        url: `/saves/${saveId}/event-templates`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'EventTemplate', id: 'LIST' }],
    }),

    updateEventTemplate: builder.mutation<
      { id: number; name: string; color?: string; icon?: string; message: string },
      { id: number; saveId: string; payload: UpdateEventTemplatePayload }
    >({
      query: ({ id, saveId, payload }) => ({
        url: `/saves/${saveId}/event-templates/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EventTemplate', id },
        { type: 'EventTemplate', id: 'LIST' },
      ],
    }),

    deleteEventTemplate: builder.mutation<
      { id: number; message: string },
      { id: number; saveId: string }
    >({
      query: ({ id, saveId }) => ({
        url: `/saves/${saveId}/event-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'EventTemplate', id },
        { type: 'EventTemplate', id: 'LIST' },
      ],
    }),

    getEventTemplateStats: builder.query<EventTemplateStatsResponse[], { saveId: string }>({
      query: ({ saveId }) => ({
        url: `/saves/${saveId}/event-templates/stats`,
        method: 'GET',
      }),
      providesTags: [{ type: 'EventTemplate', id: 'STATS' }],
    }),
  }),
});

export const {
  useGetEventTemplatesQuery,
  useGetEventTemplateByIdQuery,
  useCreateEventTemplateMutation,
  useUpdateEventTemplateMutation,
  useDeleteEventTemplateMutation,
  useGetEventTemplateStatsQuery,
} = eventTemplatesApi;
